/**
 * Iter 75 — Security Center
 *
 * Admin-only page surfacing:
 *   • Top stats (active sessions, failed logins 24h, rate-limit hits)
 *   • Active sessions table with "Force logout" action per session
 *   • Login history (success + failed) with IP
 *   • Recent security events (rate-limit hits + revocations)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Shield, Activity, AlertTriangle, LogOut, Loader2, RefreshCw,
  Users as UsersIcon, ShieldOff, Clock, Filter, Globe, Unlock, Save, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { useAuth } from '../../contexts/AuthContext';

function fmtRelative(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleString('en-IN', { dateStyle: 'medium' });
  } catch {
    return iso;
  }
}

function StatTile({ icon: Icon, label, value, color = 'slate', testid }) {
  const colors = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    indigo: 'bg-teal-50 text-teal-700 border-teal-200',
  };
  return (
    <div className={`${colors[color]} border rounded-sm p-4 flex flex-col gap-1`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] font-display font-bold uppercase tracking-widest">
        <Icon size={12} /> {label}
      </div>
      <div className="text-2xl font-bold font-mono">{value ?? '—'}</div>
    </div>
  );
}

export default function SecurityCenter() {
  const { user } = useAuth() || {};
  const isSuperAdmin = user?.role === 'super_admin';
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [logins, setLogins] = useState([]);
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [policyDirty, setPolicyDirty] = useState(false);
  const [policySaving, setPolicySaving] = useState(false);
  const [confirm, ConfirmHost] = useConfirm();

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, sess, hist, ev, pol] = await Promise.all([
        apiGet('/security/stats'),
        apiGet('/security/sessions'),
        apiGet(`/security/login-history${onlyFailed ? '?only_failed=true' : ''}`),
        apiGet('/security/events'),
        apiGet('/security/policy').catch(() => null),
      ]);
      setStats(s);
      setSessions(sess.sessions || []);
      setLogins(hist.rows || []);
      setEvents(ev);
      if (pol) { setPolicy(pol.policy); setPolicyDirty(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Security Center');
    } finally {
      setLoading(false);
    }
  }, [onlyFailed]);

  useEffect(() => { reload(); }, [reload]);

  const setPolicyField = (k, v) => {
    setPolicy(prev => ({ ...(prev || {}), [k]: v }));
    setPolicyDirty(true);
  };

  const savePolicy = async () => {
    if (!policy) return;
    setPolicySaving(true);
    try {
      const r = await apiPut('/security/policy', policy);
      setPolicy(r.policy);
      setPolicyDirty(false);
      toast.success('Security policy updated. New rules apply immediately.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save security policy');
    } finally {
      setPolicySaving(false);
    }
  };

  const onForceLogout = async (session) => {
    const ok = await confirm({
      title: `Force logout ${session.email}?`,
      body: `This will revoke ALL active sessions for ${session.email} across every device they're logged into. They will need to log back in.`,
      confirmLabel: 'Force Logout',
      danger: true,
    });
    if (!ok) return;
    try {
      const r = await apiPost(`/security/users/${session.user_id}/force-logout`, {});
      toast.success(`Revoked ${r.revoked} session${r.revoked === 1 ? '' : 's'} for ${session.email}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Force logout failed');
    }
  };

  const onClearLockout = async (email) => {
    if (!email) return;
    const ok = await confirm({
      title: `Clear lockout for ${email}?`,
      body: `This removes all failed-login attempts for this email so they can log in immediately. Use after a password reset or when a user is wrongly locked out.`,
      confirmLabel: 'Clear Lockout',
    });
    if (!ok) return;
    try {
      // Look up the user by email to get user_id
      const userResp = await apiGet(`/user-management/users`);
      const target = (userResp.users || []).find(u => (u.email || '').toLowerCase() === email.toLowerCase());
      if (!target) {
        toast.error(`No user account found for ${email}`);
        return;
      }
      const r = await apiPost(`/security/users/${target.user_id}/clear-lockout`, {});
      toast.success(`Cleared ${r.cleared} failed attempt${r.cleared === 1 ? '' : 's'} for ${email}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clear lockout failed');
    }
  };

  return (
    <div className="bg-[#F8F5EE] min-h-screen" data-testid="security-center">
      {ConfirmHost}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <Link to="/app/settings" className="text-[11px] font-display font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2">
              <ArrowLeft size={12} /> Back to Settings
            </Link>
            <h1 className="font-display text-2xl font-bold flex items-center gap-2">
              <Shield size={20} className="text-[#1F7A6E]" /> Security Center
            </h1>
            <p className="text-xs text-slate-500 mt-1">Active sessions · login history · revoked tokens · rate-limit events.</p>
          </div>
          <button
            onClick={reload}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider border border-slate-300 rounded-sm hover:bg-slate-50"
            data-testid="security-reload-btn"
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-6 space-y-6">
        {loading && !stats ? (
          <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Stat tiles */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <StatTile icon={UsersIcon} label="Active Sessions" value={stats?.active_sessions} color="emerald" testid="stat-active-sessions" />
              <StatTile icon={Activity} label="Logins · 24h" value={stats?.successful_logins_24h} color="indigo" testid="stat-logins-24h" />
              <StatTile icon={AlertTriangle} label="Failed · 24h" value={stats?.failed_logins_24h} color="amber" testid="stat-failed-24h" />
              <StatTile icon={ShieldOff} label="Rate-Limit Hits · 24h" value={stats?.rate_limit_hits_24h} color="rose" testid="stat-rate-limit" />
              <StatTile icon={LogOut} label="Revoked Tokens · 7d" value={stats?.revoked_tokens_7d} color="slate" testid="stat-revoked" />
              <StatTile icon={Clock} label="Unique Users · 24h" value={stats?.unique_users_logged_in_24h} color="indigo" testid="stat-unique-users" />
            </div>

            {/* Security Policy editor (super_admin only) */}
            {policy && (
              <section className="bg-white border border-slate-200 rounded-sm" data-testid="security-policy-editor">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <h2 className="font-display font-bold text-sm flex items-center gap-2">
                    <ShieldCheck size={14} className="text-[#1F7A6E]" /> Security Policy
                    <span className="text-[10px] font-mono text-slate-500">· login lockout & password rules</span>
                  </h2>
                  {isSuperAdmin && (
                    <button
                      onClick={savePolicy}
                      disabled={!policyDirty || policySaving}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider rounded-sm disabled:opacity-40 disabled:cursor-not-allowed bg-[#1F7A6E] text-white hover:bg-[#14504E]"
                      data-testid="save-policy-btn"
                    >
                      {policySaving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                      {policyDirty ? 'Save Changes' : 'No Changes'}
                    </button>
                  )}
                </div>
                <div className="p-4 grid md:grid-cols-3 gap-4 text-xs">
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">Lockout Threshold</label>
                    <input
                      type="number" min={1} max={100}
                      value={policy.lockout_threshold ?? 5}
                      onChange={e => setPolicyField('lockout_threshold', e.target.value)}
                      disabled={!isSuperAdmin}
                      className="w-full px-2.5 py-1.5 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50"
                      data-testid="policy-lockout-threshold"
                    />
                    <p className="text-[11px] text-slate-500">Number of failed login attempts before lockout. Default: 5.</p>
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">Lockout Window (minutes)</label>
                    <input
                      type="number" min={1} max={1440}
                      value={policy.lockout_window_minutes ?? 15}
                      onChange={e => setPolicyField('lockout_window_minutes', e.target.value)}
                      disabled={!isSuperAdmin}
                      className="w-full px-2.5 py-1.5 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50"
                      data-testid="policy-lockout-window"
                    />
                    <p className="text-[11px] text-slate-500">Rolling window for counting failures. Default: 15 min.</p>
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">Password Min Length</label>
                    <input
                      type="number" min={8} max={64}
                      value={policy.password_min_length ?? 12}
                      onChange={e => setPolicyField('password_min_length', e.target.value)}
                      disabled={!isSuperAdmin}
                      className="w-full px-2.5 py-1.5 border border-slate-300 rounded-sm text-sm disabled:bg-slate-50"
                      data-testid="policy-password-min"
                    />
                    <p className="text-[11px] text-slate-500">Default: 12 chars.</p>
                  </div>
                  <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-slate-100">
                    {[
                      ['password_require_lowercase', 'Require lowercase'],
                      ['password_require_uppercase', 'Require uppercase'],
                      ['password_require_digit', 'Require digit'],
                      ['password_require_special', 'Require special char'],
                      ['password_block_common', 'Block common passwords'],
                    ].map(([k, label]) => (
                      <label key={k} className="inline-flex items-center gap-2 px-2 py-1.5 text-[12px] cursor-pointer hover:bg-slate-50 rounded-sm">
                        <input
                          type="checkbox"
                          checked={!!policy[k]}
                          onChange={e => setPolicyField(k, e.target.checked)}
                          disabled={!isSuperAdmin}
                          data-testid={`policy-${k}`}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  {!isSuperAdmin && (
                    <div className="md:col-span-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                      Read-only. Only Super Admin can change the security policy.
                    </div>
                  )}
                </div>
              </section>
            )}


            {/* Active sessions */}
            <section className="bg-white border border-slate-200 rounded-sm">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-display font-bold text-sm flex items-center gap-2">
                  <UsersIcon size={14} className="text-[#1F7A6E]" /> Active Sessions
                  <span className="text-[10px] font-mono text-slate-500">· {sessions.length} live</span>
                </h2>
              </div>
              {sessions.length === 0 ? (
                <div className="text-xs text-slate-500 italic px-4 py-6 text-center">No active sessions.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                      <tr>
                        <th className="text-left px-4 py-2 font-display font-bold">User</th>
                        <th className="text-left px-4 py-2 font-display font-bold">IP</th>
                        <th className="text-left px-4 py-2 font-display font-bold">Device</th>
                        <th className="text-left px-4 py-2 font-display font-bold">Started</th>
                        <th className="text-left px-4 py-2 font-display font-bold">Expires</th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {sessions.map((s, i) => (
                        <tr key={s.jti || i} className="hover:bg-slate-50" data-testid={`session-row-${i}`}>
                          <td className="px-4 py-2 font-mono">{s.email}</td>
                          <td className="px-4 py-2 font-mono text-[11px]"><Globe size={10} className="inline mr-1 text-slate-400" />{s.ip || '—'}</td>
                          <td className="px-4 py-2 text-[11px] text-slate-600 truncate max-w-[280px]" title={s.user_agent}>{s.user_agent || '—'}</td>
                          <td className="px-4 py-2 text-slate-600">{fmtRelative(s.created_at)}</td>
                          <td className="px-4 py-2 text-slate-600">{fmtRelative(s.expires_at)}</td>
                          <td className="px-4 py-2 text-right">
                            <button
                              onClick={() => onForceLogout(s)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-display font-bold uppercase tracking-wider text-rose-700 hover:bg-rose-50 rounded-sm border border-rose-200"
                              data-testid={`force-logout-${i}`}
                            >
                              <LogOut size={9} /> Force Logout
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Login history */}
            <section className="bg-white border border-slate-200 rounded-sm">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-display font-bold text-sm flex items-center gap-2">
                  <Activity size={14} className="text-[#1F7A6E]" /> Login History
                  <span className="text-[10px] font-mono text-slate-500">· {logins.length} shown</span>
                </h2>
                <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={onlyFailed}
                    onChange={(e) => setOnlyFailed(e.target.checked)}
                    className="w-3 h-3"
                    data-testid="filter-only-failed"
                  />
                  <Filter size={11} /> Show only failed attempts
                </label>
              </div>
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-2 font-display font-bold">Email</th>
                      <th className="text-left px-4 py-2 font-display font-bold">IP</th>
                      <th className="text-left px-4 py-2 font-display font-bold">Status</th>
                      <th className="text-left px-4 py-2 font-display font-bold">When</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {logins.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-4 text-center text-slate-400 italic">No login attempts to show.</td></tr>
                    ) : logins.map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-4 py-1.5 font-mono">{r.email || '—'}</td>
                        <td className="px-4 py-1.5 font-mono text-[11px]">{r.ip || '—'}</td>
                        <td className="px-4 py-1.5">
                          {r.success ? (
                            <span className="tag bg-emerald-50 text-emerald-700 text-[9px]">SUCCESS</span>
                          ) : (
                            <span className="tag bg-rose-50 text-rose-700 text-[9px]">FAILED</span>
                          )}
                        </td>
                        <td className="px-4 py-1.5 text-slate-600">{fmtRelative(r.at)}</td>
                        <td className="px-4 py-1.5 text-right">
                          {!r.success && r.email && (
                            <button
                              onClick={() => onClearLockout(r.email)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-display font-bold uppercase tracking-wider text-emerald-700 hover:bg-emerald-50 rounded-sm border border-emerald-200"
                              data-testid={`clear-lockout-${i}`}
                              title="Clear all failed-login attempts for this email"
                            >
                              <Unlock size={9} /> Clear
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Rate-limit hits + revocations */}
            <div className="grid md:grid-cols-2 gap-4">
              <section className="bg-white border border-slate-200 rounded-sm">
                <div className="px-4 py-3 border-b border-slate-200">
                  <h2 className="font-display font-bold text-sm flex items-center gap-2">
                    <ShieldOff size={14} className="text-rose-600" /> Rate-Limit Events · 24h
                    <span className="text-[10px] font-mono text-slate-500">· {events?.rate_limit_count || 0}</span>
                  </h2>
                </div>
                <div className="max-h-[280px] overflow-y-auto">
                  {(events?.rate_limit_events || []).length === 0 ? (
                    <div className="text-xs italic text-slate-400 text-center py-6">No rate-limit hits in the last 24 hours.</div>
                  ) : (
                    <ul className="divide-y divide-slate-100 text-xs">
                      {events.rate_limit_events.map((e, i) => (
                        <li key={i} className="px-4 py-1.5 flex items-center justify-between hover:bg-slate-50">
                          <span className="font-mono truncate flex-1">{e.key}</span>
                          <span className="text-slate-500 text-[11px] shrink-0 ml-2">{fmtRelative(e.ts)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>

              <section className="bg-white border border-slate-200 rounded-sm">
                <div className="px-4 py-3 border-b border-slate-200">
                  <h2 className="font-display font-bold text-sm flex items-center gap-2">
                    <LogOut size={14} className="text-slate-600" /> Recent Token Revocations
                    <span className="text-[10px] font-mono text-slate-500">· {events?.revocations_count || 0}</span>
                  </h2>
                </div>
                <div className="max-h-[280px] overflow-y-auto">
                  {(events?.revocations || []).length === 0 ? (
                    <div className="text-xs italic text-slate-400 text-center py-6">No revocations on record.</div>
                  ) : (
                    <ul className="divide-y divide-slate-100 text-xs">
                      {events.revocations.map((r, i) => (
                        <li key={i} className="px-4 py-1.5 hover:bg-slate-50">
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-[11px] truncate">user: {r.user_id || '—'}</span>
                            <span className="text-slate-500 text-[10px] shrink-0 ml-2">{fmtRelative(r.revoked_at)}</span>
                          </div>
                          {r.reason && <div className="text-[10px] text-slate-400 mt-0.5">{r.reason}</div>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
